import {IFunctionContainer} from '@mintdata-wsp/common/state/ui/visualDesigner/spreadsheet/ClientFunction';
import {TextUtil} from '@mintdata-wsp/common/util/TextUtil';
import {ABoolean} from '@mintdata-wsp/mint-kit/api/values/ABoolean';
import {Function, Param} from '@mintdata/mint-kit/api/decorators/Function';
import {SheetType} from '@mintdata/mint-kit/api/functions/SheetAPI';
import {Type} from '@mintdata/mint-kit/api/types/Type';
import {IValue} from '@mintdata/mint-kit/api/types/Value';
import {AEmpty} from '@mintdata/mint-kit/api/values/AEmpty';
import {AError} from '@mintdata/mint-kit/api/values/AError';
import {AInteger} from '@mintdata/mint-kit/api/values/AInteger';
import {AString} from '@mintdata/mint-kit/api/values/AString';
import {IFrame} from '@mintdata/mint-kit/api/values/frames/Frame';
import {FrameField} from '@mintdata/mint-kit/api/values/frames/FrameField';
import {Frames} from '@mintdata/mint-kit/api/values/frames/Frames';
import {Assert} from '@mintdata/mint-kit/impl/util/Assert';
import {JSON_ITEM_FIELD_NAME, JsonUtil} from '@mintdata/mint-kit/impl/util/JsonUtil';
import bind from 'bind-decorator';
import * as jp from 'jsonpath/jsonpath.min';
import _isEmpty from 'lodash/isEmpty';
import {XmlParserUtil} from './util/xml/XmlParserUtil';

const GRAB_EXPR_SINGLE_AS_RE = /^(.*?)(?:\s+as\s+('(?:[^'\\]|\\')+'|[^\s']+))?$/;
const GRAB_EXPR_LIST_AS_RE = /^(.*?)\s*->\s*(.*)$/;
const AS_CLAUSE_LIST_RE = /\s*('(?:[^'\\]|\\')+'|[^\s']+)\s+as\s+('(?:[^'\\]|\\')+'|[^\s']+)\s*(?:,|$)/g;

interface ColumnsRenaming
{
    singleDest?:string,
    srcToDestMap:any
}

interface GrabExprParseResult extends ColumnsRenaming
{
    jsonPath:string
}

export class JsonFunctions implements IFunctionContainer
{
    public get namespace()
    {
        return 'json';
    }

    @Function({
        title: 'JSON(frame)',
        tip: 'Converts a frame into json string. ' +
            'Treats single-row frame as an object, unless frame has __force_list column - in which case, frame will be converted to single-value json array.‌',
        alias: 'JSON',
        example: 'JSON(A1)',
        icon: '',
        returns:
            {
                title: 'json',
                tip: 'This function always returns text according to a specified format',
                type: Type.String,
            }
    })
    @bind
    public json(
        @Param({
            title: 'frame',
            tip: 'The frame to convert.',
            type: Type.Frame
        }) frame:IFrame):AString
    {
        return new AString(JsonUtil.convertFrameToJson(frame));
    }

    @Function({
        title: 'JSON_PRETTY(jsonString, [indent=4])',
        tip: 'Takes valid JSON as input, and "pretty prints it.‌',
        alias: 'JSON_PRETTY',
        example: 'JSON_PRETTY(A1, 2)',
        icon: '',
        returns:
            {
                title: 'json',
                tip: 'This function always returns text according to a specified format',
                type: Type.String,
            }
    })
    @bind
    public jsonPretty(
        @Param({
            title: 'jsonString',
            tip: 'The string to format',
            type: Type.String
        }) jsonString:AString,
        @Param({
            title: 'indent',
            tip: 'indent to use',
            type: Type.Integer,
            optional: true
        }) indent:AInteger):AString | AError
    {
        const aIndent = indent ? indent.value : 4;
        const json = jsonString.value;

        try
        {
            return new AString(JSON.stringify(JSON.parse(json), null, aIndent));
        }
        catch( e )
        {
            return jsonString;
        }
    }

    @Function({
        title: 'FROM_JSON(string)',
        tip: 'Converts a json string to frame.',
        alias: 'FROM_JSON',
        example: 'FROM_JSON(A1)',
        icon: '',
        returns:
            {
                title: 'frame',
                tip: 'Returns resulting frame',
                type: Type.Frame,
            }
    })
    @bind
    public fromJson(
        @Param({
            title: 'json',
            tip: 'The json to convert.',
            type: Type.String
        }) json:AString):IFrame | AError
    {

        let jsObject;
        try
        {
            jsObject = JSON.parse(json.value);
        }
        catch( e )
        {
            return new AError('#JSON_PARSE_ERROR');
        }

        return JsonUtil.convertJsonObjectToFrame(jsObject);
    }

    @Function({
        title: 'grab(inputFrame, [grabexprs1], [grabexprs2], ...)',
        tip: 'Returns a result of applying JSONPath expression to the input frame. ' +
            'If multiple expressions are specified, their results will be merged.',
        alias: 'GRAB',
        example: 'GRAB(A1, "$[0].store[*].book")',
        icon: '',
        returns:
            {
                type: SheetType.AnyIValue,
                title: 'Cell value or frame',
                tip: 'Returns value or frame, that is the result of jsonpath search',
            }
    })
    @bind
    public grab(
        @Param({
            title: 'inputFrame',
            tip: 'The data to run jsonpath on.',
            type: SheetType.AnyIValue
        }) inputFrame:IFrame | AError,
        @Param({
            title: 'grabexprs',
            tip: 'grabexprs is jsonpaths with optional match renaming "as name"',
            type: SheetType.AnyIValue,
            repeatable: true
        }) args:IValue[]):IValue | AError
    {
        return this._doGrab(inputFrame, args, true, true);
    }

    @Function({
        title: 'grabList(inputFrame, [grabexpr1], [grabexpr2], ...)',
        tip: 'Returns a result of applying jsonpath expression to the input frame.' +
            'If multiple expressions will be spesified - their results will be merged.' +
            'Allways wraps result in an array - even if it\'s a single value - ' +
            'usefull in some cases, where the data is inconsistent',
        alias: 'GRAB_LIST',
        example: 'GRAB_LIST(A1, "$..store..book..author as author", "$..store..book..title as title")',
        icon: '',
        returns:
            {
                type: Type.Frame,
                title: 'Resulting frame',
                tip: 'Returns frame, that is the result of jsonpath search',
            }
    })
    @bind
    public grabList(
        @Param({
            title: 'inputFrame',
            tip: 'The data to run jsonpath on.',
            type: Type.Frame
        }) inputFrame:IFrame,
        @Param({
            title: 'grabexprs',
            tip: 'grabexprs is jsonpaths with optional match renaming "as name"',
            type: SheetType.AnyIValue,
            repeatable: true
        }) args:IValue[]):IValue | AError
    {
        return this._doGrab(inputFrame, args, false);
    }

    private _doGrab(inputFrame:IFrame | AError, args:Array<IValue>, unwrapSingleValue:boolean, isForceList:boolean = false):IValue | AError
    {
        if( !(Type.isInstanceOf(inputFrame, Type.Frame) || (Type.isInstanceOf(inputFrame, Type.Error))) ) 
            return AString.fromString('');

        const grabExprsOrError = TextUtil.collectStringsFromIValues(args);
        if( Type.isInstanceOf(grabExprsOrError, Type.Error) )
            return grabExprsOrError as AError;

        const grabExprs = grabExprsOrError as Array<string>;
        if( grabExprs.length == 0 )
            return new AError('#JSONPATH_NOT_SPECIFIED');

        let frame:IFrame;
        if( Type.isInstanceOf(inputFrame, Type.Frame) )
            frame = inputFrame as IFrame;
        else if( Type.isInstanceOf(inputFrame, Type.Error) )
            frame = Frames.forErrorWithContext(inputFrame as AError, (inputFrame as AError).errorContext);

        const inputJson = JsonUtil.convertFrameToJsonObject(frame, isForceList);

        const parseResults:Array<GrabExprParseResult> = [];
        const grabResults:Array<any> = [];
        for( let grabExpr of grabExprs )
        {
            const parseResultOrError = this._parseGrabExpr(grabExpr);
            if( Type.isInstanceOf(parseResultOrError, Type.Error) )
                return parseResultOrError as AError;

            const parseResult = parseResultOrError as GrabExprParseResult;
            const jsonPath = parseResult.jsonPath;

            let grabResult;
            try
            {
                grabResult = jp.query(inputJson, jsonPath);
            }
            catch( e )
            {
                return new AError('#INVALID_JSON_PATH');
            }

            if( unwrapSingleValue && grabResult.length == 1 )
                grabResult = grabResult[0];

            grabResults.push(grabResult);
            parseResults.push(parseResult);
        }

        //base case: only 1 expression without column renamings => return the IValue directly
        if( parseResults.length === 1 && !parseResults[0].singleDest && _isEmpty(parseResults[0].srcToDestMap) )
            return JsonUtil.convertJsonValueToIValue(grabResults[0]);

        //all other cases: => wrap to frame
        let grabbedFrames = grabResults.map(gr => JsonUtil.convertJsonObjectToFrame(gr));
        return this._mergeFramesWithRenamings(grabbedFrames, parseResults);
    }

    private _mergeFramesWithRenamings(frames:Array<IFrame>, renamings:Array<ColumnsRenaming>):IFrame | AError
    {
        Assert.yes(frames.length === renamings.length);

        // handle special case of renaming to one name, when frame actually has multiple fields
        // in that case, wrap every row to separate frame first
        for( let i = 0; i < frames.length; ++i )
        {
            const renaming = renamings[i];
            const frame = frames[i];

            if( renaming.singleDest && frame.fieldCount > 1 )
            {
                //=> special case of renaming to one name, when frame actually has multiple fields
                // in that case, wrap every row to separate frame and use them as items of this one column
                const newFields = [new FrameField(JSON_ITEM_FIELD_NAME, Type.String)];
                const newData = [];
                for( const dataRow of frame.getDataUnsafe() )
                {
                    // clone fields
                    const rowFrameFields = frame.getFields();
                    newData.push(
                        [Frames.fromFieldsAndData(rowFrameFields, [dataRow])]
                    );
                }

                frames[i] = Frames.fromFieldsAndData(newFields, newData);

                // return new AError('#SINGLE_AS_CLAUSE_CANT_BE_USED');
            }
        }

        // check that resulting field names are unique
        const destFieldsNamesMap = {};
        for( let i = 0; i < frames.length; ++i )
        {
            const frame = frames[i];
            const renaming = renamings[i];
            const fieldNames = frame.getFieldNames();

            Assert.no(renaming.singleDest && frame.fieldCount > 1, 'this should have been handled above');

            for( let fieldName of fieldNames )
            {
                const destName = renaming.singleDest ||
                    renaming.srcToDestMap[fieldName] ||
                    fieldName;
                if( destFieldsNamesMap[destName] )
                    return new AError('#COLUMN_NAMES_NOT_UNIQUE');

                destFieldsNamesMap[destName] = true;
            }
        }

        //... now do the merge
        let resultFields = [];
        let resultData = [];
        for( let i = 0; i < frames.length; ++i )
        {
            const frame = frames[i];
            const renaming = renamings[i];
            const records = frame.getRecords();
            const fields = frame.getFields();

            // currentRecords.length > resultData.length => create more resultData rows filled with AEmpty
            for( let j = resultData.length; j < records.length; ++j )
                resultData.push(AEmpty.array(resultFields.length));

            // currentRecords.length < resultData.length => add AEmpty values to ending resultData rows
            for( let j = records.length; j < resultData.length; ++j )
                resultData[j].push(...AEmpty.array(fields.length));

            // now join records in current frame to resultData
            for( let j = 0; j < records.length; ++j )
                resultData[j].push(...records[j].getCells());

            // create fields for resulting frame
            for( let j = 0; j < fields.length; ++j )
            {
                const field = fields[j];
                const destName = renaming.singleDest ||
                    renaming.srcToDestMap[field.name] ||
                    field.name;

                resultFields.push(new FrameField(destName, field.type));
            }
        }

        return Frames.fromFieldsAndData(resultFields, resultData);
    }

    private _parseGrabExpr(grabExpr:string):GrabExprParseResult | AError
    {
        const srcToDestMap = {};
        const destToSrcMap = {};

        // check if it's a "as-list" variant
        const asListMatch = grabExpr.match(GRAB_EXPR_LIST_AS_RE);
        if( asListMatch )
        {
            const jsonPath = asListMatch[1];
            const asClauseList = asListMatch[2];

            //(sic!): RegExp object isn't immutable in JS, so we need to reset it explicitly
            AS_CLAUSE_LIST_RE.lastIndex = 0;

            let lastMatch = '';
            let lastMatchIndex = 0;
            let asClauseMatch = AS_CLAUSE_LIST_RE.exec(asClauseList);
            while( asClauseMatch )
            {
                const srcName = TextUtil.stripQuotesInStringLiteral(asClauseMatch[1]);
                const destName = TextUtil.stripQuotesInStringLiteral(asClauseMatch[2]);

                // we don't allow duplicates in src columns AND in dest columns
                if( srcToDestMap[srcName] || destToSrcMap[destName] )
                    return new AError('#COLUMN_NAMES_NOT_UNIQUE');

                srcToDestMap[srcName] = destName;
                destToSrcMap[destName] = srcName;

                lastMatchIndex = asClauseMatch.index;
                lastMatch = asClauseMatch[0];
                asClauseMatch = AS_CLAUSE_LIST_RE.exec(asClauseList);
            }

            if( lastMatchIndex + lastMatch.length != asClauseList.length )
                return new AError('#INVALID_GRAB_EXPR');

            return {jsonPath, srcToDestMap};
        }

        //... otherwise it must be "single-as" variant
        const asSingleMatch = grabExpr.match(GRAB_EXPR_SINGLE_AS_RE);
        if( !asSingleMatch )
            return new AError('#INVALID_GRAB_EXPR');

        const jsonPath = asSingleMatch[1];
        const singleDest = TextUtil.stripQuotesInStringLiteral(asSingleMatch[2]);

        return {jsonPath, singleDest, srcToDestMap};
    }

    @Function({
        title: 'fromXml(xmlString)',
        tip: 'Converts XML string to a frame.',
        alias: 'FROM_XML',
        example: 'FROM_XML(A1)',
        icon: '',
        returns:
            {
                type: Type.Frame,
                title: 'Frame',
                tip: 'Returns a frame constructed from the XML string',
            }
    })
    @bind
    public fromXml(
        @Param({
            title: 'XML string',
            tip: 'The XML string to be converted to a frame.',
            type: Type.String,
        }) xmlString:AString):IFrame | AError
    {
        const json = XmlParserUtil.parseXml(xmlString.value);
        return JsonUtil.convertJsonObjectToFrame(json);
    }

    @Function({
        title: 'toXml(inputFrame)',
        tip: 'Converts inputFrame to XML string.',
        alias: 'TO_XML',
        example: 'TO_XML(A1)',
        icon: '',
        returns:
            {
                type: Type.String,
                title: 'XML string',
                tip: 'Returns an XML string constructed from the frame contents',
            }
    })
    @bind
    public toXml(
        @Param({
            title: 'inputFrame',
            tip: 'The data frame to be converted to an XML string.',
            type: Type.Frame,
        }) inputFrame:IFrame,
        @Param({
            title: 'prettify',
            tip: 'Prettify the result?',
            type: Type.Boolean,
            optional: true,
        }) prettify:ABoolean):AString | AError
    {
        const json:string = JsonUtil.convertFrameToJson(inputFrame);

        const xml:string = XmlParserUtil.toXml(JSON.parse(json));

        return AString.fromString(prettify ? XmlParserUtil.prettifyXml(xml) : xml);
    }
}
